import { useState, useEffect } from 'react';
import { Box, Divider, Grid, Typography, useMediaQuery, Button, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import WhiteInosio from '../../../icons/whiteinosio.svg';
import { selectAuthState, getRecordAgreement, setGlobalError, getRefreshUser, getRegisterUserV2, getPasswordLogin } from '../../../shared/sagas/auth/authSlice';
import CheckBox from '../../../icons/CheckBox.svg';
import UnCheckedBox from '../../../icons/UnCheckedBox.svg';
import TermsDialog from '../../../components/TermsDialog';
import { getInosioTermsAndConditions, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { AcceptTermsandConditionsDto, CreateApplicationV2Dto, UpsertApplicantV2Dto, UserRegistrationV2Dto } from '@inosio/payments-sdk';
import { useFormik } from 'formik';
import { InputField } from '../../../components/InputController';
import { schemaValidationApplicantRegistration, schemaValidationApplicantRegistrationType } from '../../../utils/validation';
import { selectApplicationsState } from '../sagas/pm/applicationsSlice';
import { applicantStyles } from './CustomStyles';
import { ApplicantPageStates } from '../Applicant';
import BackIcon from '../../../icons/back1.svg';
import '../../../App.css';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { createApplicantV2, createApplicationV2, selectApplicantsState } from '../sagas/applicants/applicantsSlice';

interface PropsType {
  query: URLSearchParams;
  setPageState: (pageState: ApplicantPageStates) => void;
  isCoApplicantFlow: boolean;
}

export default function ApplicantRegistration(props: PropsType) {
  const navigation = useHistory();
  const dispatch = useDispatch();
  const classes = applicantStyles();
  const hmac = props.query.get('hmac');
  const matches560 = useMediaQuery('(max-width:560px)');
  const matches900 = useMediaQuery('(max-width:900px)');

  const { propertyDetails } = useSelector(selectApplicationsState);
  const { ApplicationInviteeDetailV2Dto } = useSelector(selectApplicantsState);
  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const { contract } = useSelector(selectOnboardingApplicationState);
  const { isCreateApplicantSuccess, Applicant, isCreateApplicationSuccess, Application } = useSelector(selectApplicantsState);
  const { isFetching, userRegistrationDetails, ipaUser, userRegistrationError, errorMessage, isAcceptingTerms, isAcceptedTerms } = useSelector(selectAuthState);
  const [openInosio, setOpenInosio] = useState(false);
  const [isTsCsAccepted, setIsTsCsAccepted] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [pageError, setPageError] = useState('');
  const [checkBoxErrorText, setCheckBoxErrorText] = useState('');

  const initialValues: schemaValidationApplicantRegistrationType = {
    firstName: ApplicationInviteeDetailV2Dto?.FirstName ?? '',
    lastName: ApplicationInviteeDetailV2Dto?.LastName ?? '',
    propertyAddress: ApplicationInviteeDetailV2Dto?.ApplicationUuid ? `Co-applicant for ${ApplicationInviteeDetailV2Dto.ApplicationUuid}` : propertyDetails.propertyAddress,
    locationUuid: propertyDetails.locationUuid || null,
    email: ApplicationInviteeDetailV2Dto?.Email ?? '',
    password: '',
    confirmPassword: '',
  };

  /*
  Steps for primary applicant:
  1. make user reg call
    a. if success, move on to next step
    b. if 409, give message to login via button below and add profile instead
    c. if any other error, display
  2. Make TC audit call *(steps 2 and 3 can happen async/same time)
  3. Make call to create applicant record
    a. if success, move to next step
    b. if error, display
  4. Relog the user so it gets a new token with the applicant as the currentPrincipal
    a. if success, move to next step
  5. Make call to create application
    a. if success, go to next steps
  6. Navigate user to application page


  steps for coapplicant
  - same exact as above, just skip step 5
  - invocation of create applicant has slightly diff params

  */

  const formik = useFormik<schemaValidationApplicantRegistrationType>({
    initialValues,
    validationSchema: schemaValidationApplicantRegistration,

    //step 1, create the user
    onSubmit: async (values) => {
      //!We probabl want to ensure there is no active access token in the flow
      return (
        isTsCsAccepted &&
        dispatch({
          type: getRegisterUserV2.type,
          payload: {
            Email: values.email,
            FirstName: values.firstName.trim(),
            LastName: values.lastName.trim(),
            Password: values.password,
          } as UserRegistrationV2Dto,
        })
      );
    },
  });

  //enable the submit button
  useEffect(() => {
    if (formik.isValid) setDisableSubmit(false);
  }, [formik.isValid, disableSubmit]);

  const fireOffLogTcAuditReq = () => {
    dispatch({
      type: getRecordAgreement.type,
      payload: {
        Version: contract.InosioTsCs.Version,
        Description: contract.InosioTsCs.Description,
      } as AcceptTermsandConditionsDto,
    });
  };

  const fireOffCreatePrimaryApplicantReq = () => {
    dispatch({
      type: createApplicantV2.type,
      payload: {
        applicantDto: {
          FirstName: formik.values.firstName.trim(),
          LastName: formik.values?.lastName.trim(),
          UserUuid: ipaUser!.userUuid,
          Email: formik.values.email,
        } as UpsertApplicantV2Dto,
      },
    });
  };

  const fireOffCreateCoApplicantReq = () => {
    dispatch({
      type: createApplicantV2.type,
      payload: {
        applicantDto: {
          FirstName: formik.values.firstName.trim(),
          LastName: formik.values?.lastName.trim(),
          Email: formik.values.email,
          ApplicationUuid: ApplicationInviteeDetailV2Dto.ApplicationUuid,
          Hmac: hmac,
          Uuid: ApplicationInviteeDetailV2Dto.Uuid,
        } as UpsertApplicantV2Dto,
      },
    });
  };

  const fireOffCreateApplicationReq = () => {
    dispatch({
      type: createApplicationV2.type,
      payload: {
        PrimaryApplicantOccupantUuid: ipaUser?.currentProfile.uuid,
        PropertyAddress: propertyDetails.propertyAddress,
        LocationUuid: propertyDetails.locationUuid || null,
        OrganizationUuid: propertyGroupDetails.Uuid,
      } as CreateApplicationV2Dto,
    });
  };

  //steps 2 & 3. On regSuccess: record TC Audit and create new applicant
  useEffect(() => {
    if (isFetching) {
      setDisableSubmit(true); //disable submit button while reg request is in flight
      return;
    }

    if (!isAcceptedTerms && userRegistrationDetails?.status === 'Success' && ipaUser?.userUuid) {
      //we received something back from the api
      //the registration was success
      fireOffLogTcAuditReq();

      if (props.isCoApplicantFlow) {
        fireOffCreateCoApplicantReq();
      } else {
        fireOffCreatePrimaryApplicantReq();
      }
    }
  }, [isFetching, isAcceptedTerms, userRegistrationDetails, ipaUser, dispatch]);

  //new step 4, relog
  useEffect(() => {
    //check if the applicant creation was good and we are still on the generic user
    if (isCreateApplicantSuccess && Applicant?.Uuid && ipaUser?.userUuid && !ipaUser.currentProfile.uuid) {
      //relog
      dispatch({
        type: getPasswordLogin.type,
        payload: {
          email: formik.values.email,
          password: formik.values.password,
        },
      });
    }
  }, [dispatch, isCreateApplicantSuccess, Applicant, ipaUser]);

  //the new step 5. create application record (only if primary flow)
  useEffect(() => {
    if (!props.isCoApplicantFlow && ipaUser?.currentProfile.uuid && ipaUser?.currentProfile.uuid === Applicant?.Uuid) {
      //i must be already re-logged, so just create a new application
      fireOffCreateApplicationReq();
    }
  }, [dispatch, ipaUser, Applicant]);

  //the new step 6, Navigate user to application page.
  useEffect(() => {
    //if it's primary flow, then we do this when isCreateApplicationSuccess is true.
    let applicationUuid: string = '';
    if (isCreateApplicationSuccess && !props.isCoApplicantFlow) {
      applicationUuid = Application.Uuid;
    } else if (props.isCoApplicantFlow && ipaUser?.currentProfile.uuid && ipaUser.currentProfile.uuid === Applicant?.Uuid) {
      //if it's coapplicant flow, we do it once the relog is success
      applicationUuid = ApplicationInviteeDetailV2Dto.ApplicationUuid;
    }
    if (applicationUuid) {
      navigation.push(`/resui/applications/${applicationUuid}`);
    }
  }, [navigation, isCreateApplicationSuccess, Application, ipaUser, ApplicationInviteeDetailV2Dto, Applicant]);

  //error cases
  useEffect(() => {
    if (userRegistrationError && Object.keys(userRegistrationError).length !== 0) {
      if (userRegistrationError?.statusCode === 409) {
        dispatch(setGlobalError({ message: 'This email address is already registered. If this is your email address, please click "I have an account" below to sign in and start this application.' }));
        setDisableSubmit(false);
      } else if (userRegistrationError?.statusCode === 403) {
        setDisableSubmit(false);
        setPageError(userRegistrationError?.message);
      } else {
        setDisableSubmit(false);
        setPageError('Something went wrong');
      }
    }
  }, [userRegistrationError, disableSubmit, isFetching]);

  //more error cases?
  useEffect(() => {
    if (errorMessage) {
      setPageError(errorMessage);
    }
  }, [errorMessage]);

  //get terms on load
  useEffect(() => {
    if (!contract.InosioTsCs.Text) {
      dispatch({
        type: getInosioTermsAndConditions.type,
      });
    }
  }, [dispatch]);

  const checkBoxErrorMethod = () => {
    if (!isTsCsAccepted) {
      setCheckBoxErrorText('Please agree to the terms and conditions before proceeding');
    } else {
      setCheckBoxErrorText('');
    }
  };

  const handleCloseInosio = () => {
    setOpenInosio(false);
    setIsTsCsAccepted(true);
  };

  const handleGoBack = () => {
    if (propertyGroupDetails?.ApplicationsSettings?.ApplicationsPetPolicyText) {
      props.setPageState(ApplicantPageStates.PetPolicies);
    } else if (propertyGroupDetails?.ApplicationsSettings?.ApplicationsPolicyText) {
      props.setPageState(ApplicantPageStates.ApplicationPolicies);
    } else if (!ApplicationInviteeDetailV2Dto?.ApplicationUuid) {
      props.setPageState(ApplicantPageStates.PropertyAddress);
    } else {
      props.setPageState(ApplicantPageStates.Landing);
    }
    dispatch(getRefreshUser());
  };

  const handleGoToLoginPage = () => {
    props.setPageState(ApplicantPageStates.Login);
    dispatch(getRefreshUser());
  };

  return (
    <Box sx={{ backgroundColor: '#F1F0F5', width: '100vw', height: '100vh', textAlign: 'center' }}>
      <Box sx={{ backgroundColor: '#ffffff', height: 93, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '35px' }}>
        <img src={WhiteInosio} style={{ width: '65px', height: '65px' }} alt="logo" />
        <Button href="/login" className={classes.signInBtn}>
          <Typography className={classes.signInText}>Sign Up | Sign In</Typography>
        </Button>
      </Box>

      {!matches900 && (
        <Typography className={classes.largeText} sx={{ mt: 4, textAlign: 'center' }}>
          {propertyGroupDetails?.CompanyName ?? ''} Application Portal
        </Typography>
      )}

      <Box sx={{ display: 'flex', backgroundColor: '#F1F0F5', flexDirection: 'column', alignItems: 'center', mt: matches900 ? 8 : 4 }}>
        <Box className={classes.formContainer} sx={{ width: '100%', maxWidth: 472, padding: '30px 0 40px', backgroundColor: matches560 ? '#F1F0F5' : '#ffffff' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="go to prev"
              onClick={handleGoBack}
              sx={{
                position: 'absolute',
                left: matches560 ? 20 : 30,
                top: '-8px',
              }}
            >
              <img src={BackIcon} alt="back" style={{ cursor: 'pointer' }} />
            </IconButton>
            <Typography className={classes.largeText} sx={{ mb: 4.5, textAlign: 'center', fontSize: matches560 ? '16px !important' : '18px' }}>
              Create an account to get started
            </Typography>
          </Box>

          {pageError.length ? (
            <div className={classes.pageError} style={{ maxWidth: '320px', padding: '12px 10px' }}>
              <Typography
                style={{
                  fontFamily: 'BMWHelvetica',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: '#DB3939',
                }}
              >
                {' '}
                {pageError}
              </Typography>
            </div>
          ) : (
            <></>
          )}

          <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ maxWidth: '340px' }}>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} sm={6} id="firstname-input">
                <InputField
                  name={'firstName'}
                  label={'First Name'}
                  value={formik.values.firstName}
                  isError={false}
                  errors={formik.errors?.firstName}
                  touched={formik.touched?.firstName}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue('firstName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} id="lastname-input">
                <InputField
                  name={'lastName'}
                  label={'Last Name'}
                  value={formik.values.lastName}
                  isError={false}
                  errors={formik.errors?.lastName}
                  touched={formik.touched?.lastName}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue('lastName', e.target.value)}
                />
              </Grid>
            </Grid>
            <InputField
              name={'email'}
              placeholder={'Email'}
              label={'Email'}
              value={formik.values.email}
              isError={false}
              errors={formik.errors?.email}
              touched={formik.touched?.email}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('email', e.target.value)}
            />
            <InputField
              name={'password'}
              placeholder={'Password'}
              type="password"
              label={'Password'}
              isError={false}
              errors={formik.errors?.password}
              touched={formik.touched?.password}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('password', e.target.value)}
            />
            <InputField
              name={'confirmPassword'}
              type="password"
              label={'Confirm Password'}
              isError={false}
              errors={formik.errors?.confirmPassword}
              touched={formik.touched?.confirmPassword}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue('confirmPassword', e.target.value)}
            />
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                style={{
                  fontFamily: 'Work Sans',
                  paddingLeft: '18px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '22px',
                  marginTop: '10px',
                }}
              >
                Accept{' '}
                <span
                  style={{
                    cursor: 'pointer',
                  }}
                  className={classes.termsAndConditionLink}
                  onClick={() => {
                    setOpenInosio(true);
                  }}
                >
                  T&Cs
                </span>
              </Typography>
              <Grid ml={1} mt={1.3} item sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <img
                  id="accept-terms"
                  src={isTsCsAccepted ? CheckBox : UnCheckedBox}
                  style={{ width: '13px', height: '13px', cursor: 'pointer' }}
                  alt="Checkmark"
                  onClick={() => setIsTsCsAccepted(!isTsCsAccepted)}
                />
              </Grid>
            </Grid>
            {checkBoxErrorText.length ? (
              <div className={classes.checkboxError}>
                <Typography
                  style={{
                    fontFamily: 'BMWHelvetica',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: '#DB3939',
                  }}
                >
                  {' '}
                  {checkBoxErrorText}
                </Typography>
              </div>
            ) : (
              <></>
            )}
            <div className={classes.buttonContainer}>
              <div className={classes.wrapper}>
                <Button
                  onClick={handleGoToLoginPage}
                  disabled={disableSubmit || isFetching}
                  variant="outlined"
                  sx={{
                    width: '183px',
                    height: '49px',
                    borderRadius: '24px',
                    textTransform: 'none',
                    color: '#39A2DB',
                    transition: 'background 0.55s, color 0.55s',
                    borderColor: '#39A2DB',
                    '&:hover': {
                      backgroundColor: '#39A2DB',
                      color: 'white',
                      borderColor: '#39A2DB',
                    },
                    fontSize: '18px',
                    lineHeight: '22px',
                  }}
                >
                  I have an account
                </Button>
              </div>
              <div className={classes.wrapper}>
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    width: '113px',
                    height: '49px',
                    borderRadius: '24px',
                    textTransform: 'capitalize',
                    color: 'white',
                    backgroundColor: '#39A2DB',
                    borderColor: '#39A2DB',
                    fontFamily: 'Open Sans',
                    fontWeight: '700',
                    fontSize: '18px',
                    lineHeight: '22px',
                  }}
                  onClick={checkBoxErrorMethod}
                  id="Sign-Up"
                >
                  Sign Up
                </Button>
                {isFetching || isAcceptingTerms ? <CircularProgress size={26} className={classes.buttonProgress} /> : <></>}
              </div>
            </div>
          </Box>
        </Box>
        {matches900 ? (
          <></>
        ) : (
          <Grid item sx={{ width: '100%', maxWidth: 523, mt: 9 }}>
            <Divider />
            <Grid item display="flex" justifyContent="center">
              <span
                className={classes.forgetText}
                onClick={() => {
                  window.open('http://support.inosio.com/');
                }}
              >
                {'Need help?'}
              </span>
            </Grid>
          </Grid>
        )}
      </Box>

      <TermsDialog open={openInosio} scroll={true} text={contract.InosioTsCs.Text} title="Inosio Agreement" handleConfirm={handleCloseInosio} buttonText="Agree" />
    </Box>
  );
}
